import { InjectionToken, Signal } from '@angular/core';
import {
  AddOrganizationAdminUser,
  AppealSubmission,
  BasePermission,
  CancelSubmission,
  ConfigurationAdmin,
  DocumentSuperAdmin,
  OrganizationAdmin,
  PeopleAdmin,
  PermissionGrants,
  QuestionnaireAdmin,
  RemoveOrganizationAdminUser,
  RoleAdmin,
  ViewOrganization,
  ViewOrganizationAdminUser,
  ViewOrganizationDetail,
  ViewOrganizationSites,
  ViewOrganizationStudies,
} from '@shared/interfaces/permissions.interface';
import { RolePermissionProvider } from '../../shared/types';

const _generateInjectionToken = <T>(uniqueKey: string): InjectionToken<T> =>
  new InjectionToken<T>(uniqueKey);

/**
 * @DOCUMENT_SUPER_ADMIN_TOKENS START
 * **/
export const DOCUMENT_SUPER_ADMIN_CANDELETE_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'DOCUMENT_SUPER_ADMIN_CANDELETE_PERMISSION',
  );

export const DOCUMENT_SUPER_ADMIN_PERMISSIONS = (): RolePermissionProvider => {
  const _DOCUMENT_SUPER_ADMIN_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>(
      'DOCUMENT_SUPER_ADMIN_BASE_PERMISSION',
    );
  const _DOCUMENT_SUPER_ADMIN_PERMISSION_DELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'DOCUMENT_SUPER_ADMIN_PERMISSION_DELETE_GRANT',
    );

  return {
    basePermissions: [
      {
        token: _DOCUMENT_SUPER_ADMIN_BASE_PERMISSION,
        tokenValue: DocumentSuperAdmin,
      },
    ],
    grants: [
      {
        token: _DOCUMENT_SUPER_ADMIN_PERMISSION_DELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
    ],
    providers: [
      {
        basePermissionToken: _DOCUMENT_SUPER_ADMIN_BASE_PERMISSION,
        grantsToken: _DOCUMENT_SUPER_ADMIN_PERMISSION_DELETE_GRANT,
        provider: DOCUMENT_SUPER_ADMIN_CANDELETE_PERMISSION,
      },
    ],
  };
};
/**
 * @DOCUMENT_SUPER_ADMIN_TOKENS END
 * **/

/**
 * @SUBMISSION PERMISSIONS_TOKENS START
 * */

export const SUBMISSION_CAN_CANCEL_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('SUBMISSION_CAN_CANCEL_PERMISSION');

export const SUBMISSION_PERMISSIONS = (): RolePermissionProvider => {
  const _SUBMISSION_BASE_PERMISSION = _generateInjectionToken<BasePermission>(
    'SUBMISSION_BASE_PERMISSION',
  );
  const _SUBMISSION_PERMISSION_CANCEL_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'SUBMISSION_PERMISSION_CANCEL_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _SUBMISSION_BASE_PERMISSION,
        tokenValue: CancelSubmission,
      },
    ],
    grants: [
      {
        token: _SUBMISSION_PERMISSION_CANCEL_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
    ],
    providers: [
      {
        basePermissionToken: _SUBMISSION_BASE_PERMISSION,
        grantsToken: _SUBMISSION_PERMISSION_CANCEL_GRANT,
        provider: SUBMISSION_CAN_CANCEL_PERMISSION,
      },
    ],
  };
};

/**
 * @ROLE_ADMIN_PERMISSION_TOKENS START
 */

export const ROLE_CANDELETE_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('ROLE_CANDELETE_PERMISSION');

export const ROLE_CANEDIT_PERMISSION = _generateInjectionToken<Signal<boolean>>(
  'ROLE_CANEDIT_PERMISSION',
);

export const ROLE_ADMIN_PERMISSIONS = (): RolePermissionProvider => {
  const _ROLE_BASE_PERMISSION = _generateInjectionToken<BasePermission>(
    '_ROLE_BASE_PERMISSION',
  );
  const _ROLE_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'ROLE_PERMISSION_EDIT_GRANT',
    );
  const _ROLE_PERMISSION_DELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'ROLE_PERMISSION_DELETE_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _ROLE_BASE_PERMISSION,
        tokenValue: RoleAdmin,
      },
    ],
    grants: [
      {
        token: _ROLE_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
      {
        token: _ROLE_PERMISSION_DELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
    ],
    providers: [
      {
        basePermissionToken: _ROLE_BASE_PERMISSION,
        grantsToken: _ROLE_PERMISSION_EDIT_GRANT,
        provider: ROLE_CANEDIT_PERMISSION,
      },
      {
        basePermissionToken: _ROLE_BASE_PERMISSION,
        grantsToken: _ROLE_PERMISSION_DELETE_GRANT,
        provider: ROLE_CANDELETE_PERMISSION,
      },
    ],
  };
};
/**
 * @ROLE_ADMIN_PERMISSION_TOKENS END
 */

/**
 * @CONFIGURATION_ADMIN_TOKENS START
 * */
export const CONFIGURATION_CANDELETE_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('CONFIGURATION_CANDELETE_PERMISSION');
export const CONFIGURATION_CANEDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('CONFIGURATION_CANEDIT_PERMISSION');
export const CONFIGURATION_ADMIN_PERMISSIONS = (): RolePermissionProvider => {
  const _CONFIGURATION_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>('CONFIGURATION_BASE_PERMISSION');
  const _CONFIGURATION_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'CONFIGURATION_PERMISSION_EDIT_GRANT',
    );
  const _CONFIGURATION_PERMISSION_DELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'CONFIGURATION_PERMISSION_DELETE_GRANT',
    );

  return {
    basePermissions: [
      {
        token: _CONFIGURATION_BASE_PERMISSION,
        tokenValue: ConfigurationAdmin,
      },
    ],
    grants: [
      {
        token: _CONFIGURATION_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
      {
        token: _CONFIGURATION_PERMISSION_DELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
    ],
    providers: [
      {
        basePermissionToken: _CONFIGURATION_BASE_PERMISSION,
        grantsToken: _CONFIGURATION_PERMISSION_EDIT_GRANT,
        provider: CONFIGURATION_CANEDIT_PERMISSION,
      },
      {
        basePermissionToken: _CONFIGURATION_BASE_PERMISSION,
        grantsToken: _CONFIGURATION_PERMISSION_DELETE_GRANT,
        provider: CONFIGURATION_CANDELETE_PERMISSION,
      },
    ],
  };
};
/**
 * @CONFIGURATION_ADMIN_TOKENS END
 * */

/**
 * @APPEALS_SUBMISSION_TOKENS START
 */

export const APPEAL_SUBMISSION_CANDELETE_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('APPEAL_SUBMISSION_CANDELETE_PERMISSION');

export const APPEAL_SUBMISSION_CANEDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('APPEAL_SUBMISSION_CANEDIT_PERMISSION');

export const APPEALS_SUBMISSION_PERMISSIONS = (): RolePermissionProvider => {
  const _APPEAL_SUBMISSION_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>(
      'APPEAL_SUBMISSION_BASE_PERMISSION',
    );
  const _APPEAL_SUBMISSION_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'APPEAL_SUBMISSION_PERMISSION_EDIT_GRANT',
    );
  const _APPEAL_SUBMISSION_PERMISSION_DELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'APPEAL_SUBMISSION_PERMISSION_DELETE_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _APPEAL_SUBMISSION_BASE_PERMISSION,
        tokenValue: AppealSubmission,
      },
    ],
    grants: [
      {
        token: _APPEAL_SUBMISSION_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
      {
        token: _APPEAL_SUBMISSION_PERMISSION_DELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
    ],
    providers: [
      {
        basePermissionToken: _APPEAL_SUBMISSION_BASE_PERMISSION,
        grantsToken: _APPEAL_SUBMISSION_PERMISSION_EDIT_GRANT,
        provider: APPEAL_SUBMISSION_CANEDIT_PERMISSION,
      },
      {
        basePermissionToken: _APPEAL_SUBMISSION_BASE_PERMISSION,
        grantsToken: _APPEAL_SUBMISSION_PERMISSION_DELETE_GRANT,
        provider: APPEAL_SUBMISSION_CANDELETE_PERMISSION,
      },
    ],
  };
};
/**
 * @APPEALS_SUBMISSION_TOKENS END
 * */

/**
 * @QUESTIONNAIRE_ADMIN_TOKENS START
 * */
export const QUESTIONNAIRE_ADMIN_CANEDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('QUESTIONNAIRE_ADMIN_CANEDIT_PERMISSION');
export const QUESTIONNAIRE_ADMIN_CANDELETE_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('QUESTIONNAIRE_ADMIN_CANDELETE_PERMISSION');

export const QUESTIONNAIRE_ADMIN_PERMISSIONS = (): RolePermissionProvider => {
  const _QUESTIONNAIRE_ADMIN_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>(
      'QUESTIONNAIRE_ADMIN_BASE_PERMISSION',
    );
  const _QUESTIONNAIRE_ADMIN_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'QUESTIONNAIRE_ADMIN_PERMISSION_EDIT_GRANT',
    );
  const _QUESTIONNAIRE_ADMIN_CANDELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'QUESTIONNAIRE_ADMIN_CANDELETE_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _QUESTIONNAIRE_ADMIN_BASE_PERMISSION,
        tokenValue: QuestionnaireAdmin,
      },
    ],
    grants: [
      {
        token: _QUESTIONNAIRE_ADMIN_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
      {
        token: _QUESTIONNAIRE_ADMIN_CANDELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
    ],
    providers: [
      {
        basePermissionToken: _QUESTIONNAIRE_ADMIN_BASE_PERMISSION,
        grantsToken: _QUESTIONNAIRE_ADMIN_PERMISSION_EDIT_GRANT,
        provider: QUESTIONNAIRE_ADMIN_CANEDIT_PERMISSION,
      },
      {
        basePermissionToken: _QUESTIONNAIRE_ADMIN_BASE_PERMISSION,
        grantsToken: _QUESTIONNAIRE_ADMIN_CANDELETE_GRANT,
        provider: QUESTIONNAIRE_ADMIN_CANDELETE_PERMISSION,
      },
    ],
  };
};
/**
 * @QUESTIONNAIRE_ADMIN_TOKENS END
 * */

/**
 * @ORGANIZATION_ADMIN_TOKENS START
 * **/
export const ORGANIZATION_ADMIN_CANDELETE_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('ORGANIZATION_ADMIN_CANDELETE_PERMISSION');
export const ORGANIZATION_ADMIN_CANEDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('ORGANIZATION_ADMIN_CANEDIT_PERMISSION');
export const ORGANIZATION_ADMIN_PERMISSIONS = (): RolePermissionProvider => {
  const _ORGANIZATION_ADMIN_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>(
      'ORGANIZATION_ADMIN_BASE_PERMISSION',
    );
  const _ORGANIZATION_PERMISSION_DELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'ORGANIZATION_PERMISSION_DELETE_GRANT',
    );
  const _ORGANIZATION_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'ORGANIZATION_PERMISSION_EDIT_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _ORGANIZATION_ADMIN_BASE_PERMISSION,
        tokenValue: OrganizationAdmin,
      },
    ],
    grants: [
      {
        token: _ORGANIZATION_PERMISSION_DELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
      {
        token: _ORGANIZATION_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
    ],
    providers: [
      {
        basePermissionToken: _ORGANIZATION_ADMIN_BASE_PERMISSION,
        grantsToken: _ORGANIZATION_PERMISSION_DELETE_GRANT,
        provider: ORGANIZATION_ADMIN_CANDELETE_PERMISSION,
      },
      {
        basePermissionToken: _ORGANIZATION_ADMIN_BASE_PERMISSION,
        grantsToken: _ORGANIZATION_PERMISSION_EDIT_GRANT,
        provider: ORGANIZATION_ADMIN_CANEDIT_PERMISSION,
      },
    ],
  };
};
/**
 * @ORGANIZATION_ADMIN_TOKENS END
 * **/
/**
 * @PEOPLE_ADMIN_TOKENS START
 * **/
export const PEOPLE_ADMIN_CANDELETE_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('PEOPLE_ADMIN_CANDELETE_PERMISSION');
export const PEOPLE_ADMIN_CANEDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('PEOPLE_ADMIN_CANEDIT_PERMISSION');
export const PEOPLE_ADMIN_PERMISSIONS = (): RolePermissionProvider => {
  const _PEOPLE_ADMIN_BASE_PERMISSION = _generateInjectionToken<BasePermission>(
    'PEOPLE_ADMIN_BASE_PERMISSION',
  );
  const _PEOPLE_PERMISSION_DELETE_GRANT =
    _generateInjectionToken<PermissionGrants.CanRemove>(
      'PEOPLE_PERMISSION_DELETE_GRANT',
    );
  const _PEOPLE_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'PEOPLE_PERMISSION_EDIT_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _PEOPLE_ADMIN_BASE_PERMISSION,
        tokenValue: PeopleAdmin,
      },
    ],
    grants: [
      {
        token: _PEOPLE_PERMISSION_DELETE_GRANT,
        tokenValue: PermissionGrants.CanRemove,
      },
      {
        token: _PEOPLE_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
    ],
    providers: [
      {
        basePermissionToken: _PEOPLE_ADMIN_BASE_PERMISSION,
        grantsToken: _PEOPLE_PERMISSION_DELETE_GRANT,
        provider: PEOPLE_ADMIN_CANDELETE_PERMISSION,
      },
      {
        basePermissionToken: _PEOPLE_ADMIN_BASE_PERMISSION,
        grantsToken: _PEOPLE_PERMISSION_EDIT_GRANT,
        provider: PEOPLE_ADMIN_CANEDIT_PERMISSION,
      },
    ],
  };
};
/**
 * @PEOPLE_ADMIN_TOKENS END
 * **/

/**
 * @VIEW_ORGANIZATION_TOKENS START
 * **/
export const VIEW_ORGANIZATION_CAN_VIEW_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('VIEW_ORGANIZATION_CAN_VIEW_PERMISSION');
export const VIEW_ORGANIZATION_CAN_EDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('VIEW_ORGANIZATION_CAN_EDIT_PERMISSION');

export const VIEW_ORGANIZATION_PERMISSIONS = (): RolePermissionProvider => {
  const _VIEW_ORGANIZATION_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>(
      'VIEW_ORGANIZATION_BASE_PERMISSION',
    );
  const _VIEW_ORGANIZATION_PERMISSION_VIEW_GRANT =
    _generateInjectionToken<PermissionGrants.CanView>(
      'VIEW_ORGANIZATION_PERMISSION_VIEW_GRANT',
    );
  const _VIEW_ORGANIZATION_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'VIEW_ORGANIZATION_PERMISSION_EDIT_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _VIEW_ORGANIZATION_BASE_PERMISSION,
        tokenValue: ViewOrganization,
      },
    ],
    grants: [
      {
        token: _VIEW_ORGANIZATION_PERMISSION_VIEW_GRANT,
        tokenValue: PermissionGrants.CanView,
      },
      {
        token: _VIEW_ORGANIZATION_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
    ],
    providers: [
      {
        basePermissionToken: _VIEW_ORGANIZATION_BASE_PERMISSION,
        grantsToken: _VIEW_ORGANIZATION_PERMISSION_VIEW_GRANT,
        provider: VIEW_ORGANIZATION_CAN_VIEW_PERMISSION,
      },
      {
        basePermissionToken: _VIEW_ORGANIZATION_BASE_PERMISSION,
        grantsToken: _VIEW_ORGANIZATION_PERMISSION_EDIT_GRANT,
        provider: VIEW_ORGANIZATION_CAN_EDIT_PERMISSION,
      },
    ],
  };
};
/**
 * @VIEW_ORGANIZATION_TOKENS END
 * **/

/**
 * @VIEW_ORGANIZATION_DETAIL_TOKENS START
 * **/
export const VIEW_ORGANIZATION_DETAIL_CANVIEW_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_DETAIL_CANVIEW_PERMISSION',
  );
export const VIEW_ORGANIZATION_DETAIL_CANEDIT_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_DETAIL_CANEDIT_PERMISSION',
  );

export const VIEW_ORGANIZATION_DETAIL_PERMISSIONS =
  (): RolePermissionProvider => {
    const _VIEW_ORGANIZATION_DETAIL_BASE_PERMISSION =
      _generateInjectionToken<BasePermission>(
        'VIEW_ORGANIZATION_DETAIL_BASE_PERMISSION',
      );
    const _VIEW_ORGANIZATION_DETAIL_PERMISSION_VIEW_GRANT =
      _generateInjectionToken<PermissionGrants.CanView>(
        'VIEW_ORGANIZATION_DETAIL_PERMISSION_VIEW_GRANT',
      );
    const _VIEW_ORGANIZATION_DETAIL_PERMISSION_EDIT_GRANT =
      _generateInjectionToken<PermissionGrants.CanEdit>(
        'VIEW_ORGANIZATION_DETAIL_PERMISSION_EDIT_GRANT',
      );
    return {
      basePermissions: [
        {
          token: _VIEW_ORGANIZATION_DETAIL_BASE_PERMISSION,
          tokenValue: ViewOrganizationDetail,
        },
      ],
      grants: [
        {
          token: _VIEW_ORGANIZATION_DETAIL_PERMISSION_VIEW_GRANT,
          tokenValue: PermissionGrants.CanView,
        },
        {
          token: _VIEW_ORGANIZATION_DETAIL_PERMISSION_EDIT_GRANT,
          tokenValue: PermissionGrants.CanEdit,
        },
      ],
      providers: [
        {
          basePermissionToken: _VIEW_ORGANIZATION_DETAIL_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_DETAIL_PERMISSION_VIEW_GRANT,
          provider: VIEW_ORGANIZATION_DETAIL_CANVIEW_PERMISSION,
        },
        {
          basePermissionToken: _VIEW_ORGANIZATION_DETAIL_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_DETAIL_PERMISSION_EDIT_GRANT,
          provider: VIEW_ORGANIZATION_DETAIL_CANEDIT_PERMISSION,
        },
      ],
    };
  };
/**
 * @VIEW_ORGANIZATION_DETAIL_TOKENS END
 * **/

/**
 * @ADD_USER_ORGANIZATION_TOKENS START
 * **/
export const ADD_USER_ORGANIZATION_CANEDIT_PERMISSION = _generateInjectionToken<
  Signal<boolean>
>('ADD_USER_ORGANIZATION_CANEDIT_PERMISSION');

export const ADD_USER_ORGANIZATION_PERMISSIONS = (): RolePermissionProvider => {
  const _ADD_USER_ORGANIZATION_BASE_PERMISSION =
    _generateInjectionToken<BasePermission>(
      'ADD_USER_ORGANIZATION_BASE_PERMISSION',
    );
  const _ADD_USER_ORGANIZATION_PERMISSION_EDIT_GRANT =
    _generateInjectionToken<PermissionGrants.CanEdit>(
      'ADD_USER_ORGANIZATION_PERMISSION_EDIT_GRANT',
    );
  return {
    basePermissions: [
      {
        token: _ADD_USER_ORGANIZATION_BASE_PERMISSION,
        tokenValue: AddOrganizationAdminUser,
      },
    ],
    grants: [
      {
        token: _ADD_USER_ORGANIZATION_PERMISSION_EDIT_GRANT,
        tokenValue: PermissionGrants.CanEdit,
      },
    ],
    providers: [
      {
        basePermissionToken: _ADD_USER_ORGANIZATION_BASE_PERMISSION,
        grantsToken: _ADD_USER_ORGANIZATION_PERMISSION_EDIT_GRANT,
        provider: ADD_USER_ORGANIZATION_CANEDIT_PERMISSION,
      },
    ],
  };
};
/**
 * @ADD_USER_ORGANIZATION_TOKENS END
 * **/

/**
 * @REMOVE_USER_ORGANIZATION_TOKENS START
 * **/
export const REMOVE_USER_ORGANIZATION_CANEDIT_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'REMOVE_USER_ORGANIZATION_CANEDIT_PERMISSION',
  );
export const REMOVE_USER_ORGANIZATION_CANREMOVE_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'REMOVE_USER_ORGANIZATION_CANREMOVE_PERMISSION',
  );

export const REMOVE_USER_ORGANIZATION_PERMISSIONS =
  (): RolePermissionProvider => {
    const _REMOVE_USER_ORGANIZATION_BASE_PERMISSION =
      _generateInjectionToken<BasePermission>(
        'REMOVE_USER_ORGANIZATION_BASE_PERMISSION',
      );
    const _REMOVE_USER_ORGANIZATION_PERMISSION_EDIT_GRANT =
      _generateInjectionToken<PermissionGrants.CanEdit>(
        'REMOVE_USER_ORGANIZATION_PERMISSION_EDIT_GRANT',
      );
    const _REMOVE_USER_ORGANIZATION_PERMISSION_REMOVE_GRANT =
      _generateInjectionToken<PermissionGrants.CanRemove>(
        'REMOVE_USER_ORGANIZATION_PERMISSION_REMOVE_GRANT',
      );
    return {
      basePermissions: [
        {
          token: _REMOVE_USER_ORGANIZATION_BASE_PERMISSION,
          tokenValue: RemoveOrganizationAdminUser,
        },
      ],
      grants: [
        {
          token: _REMOVE_USER_ORGANIZATION_PERMISSION_EDIT_GRANT,
          tokenValue: PermissionGrants.CanEdit,
        },
        {
          token: _REMOVE_USER_ORGANIZATION_PERMISSION_REMOVE_GRANT,
          tokenValue: PermissionGrants.CanRemove,
        },
      ],
      providers: [
        {
          basePermissionToken: _REMOVE_USER_ORGANIZATION_BASE_PERMISSION,
          grantsToken: _REMOVE_USER_ORGANIZATION_PERMISSION_EDIT_GRANT,
          provider: REMOVE_USER_ORGANIZATION_CANEDIT_PERMISSION,
        },
        {
          basePermissionToken: _REMOVE_USER_ORGANIZATION_BASE_PERMISSION,
          grantsToken: _REMOVE_USER_ORGANIZATION_PERMISSION_REMOVE_GRANT,
          provider: REMOVE_USER_ORGANIZATION_CANREMOVE_PERMISSION,
        },
      ],
    };
  };
/**
 * @REMOVE_USER_ORGANIZATION_TOKENS END
 * **/

/**
 * @VIEW_ORGANIZATION_USER_TOKENS START
 * **/
export const VIEW_ORGANIZATION_USER_CANVIEW_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_USER_CANVIEW_PERMISSION',
  );
export const VIEW_ORGANIZATION_USER_CANEDIT_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_USER_CANEDIT_PERMISSION',
  );

export const VIEW_ORGANIZATION_USER_PERMISSIONS =
  (): RolePermissionProvider => {
    const _VIEW_ORGANIZATION_USER_BASE_PERMISSION =
      _generateInjectionToken<BasePermission>(
        'VIEW_ORGANIZATION_USER_BASE_PERMISSION',
      );
    const _VIEW_ORGANIZATION_USER_PERMISSION_VIEW_GRANT =
      _generateInjectionToken<PermissionGrants.CanView>(
        'VIEW_ORGANIZATION_USER_PERMISSION_VIEW_GRANT',
      );
    const _VIEW_ORGANIZATION_USER_PERMISSION_EDIT_GRANT =
      _generateInjectionToken<PermissionGrants.CanEdit>(
        'VIEW_ORGANIZATION_USER_PERMISSION_EDIT_GRANT',
      );
    return {
      basePermissions: [
        {
          token: _VIEW_ORGANIZATION_USER_BASE_PERMISSION,
          tokenValue: ViewOrganizationAdminUser,
        },
      ],
      grants: [
        {
          token: _VIEW_ORGANIZATION_USER_PERMISSION_VIEW_GRANT,
          tokenValue: PermissionGrants.CanView,
        },
        {
          token: _VIEW_ORGANIZATION_USER_PERMISSION_EDIT_GRANT,
          tokenValue: PermissionGrants.CanEdit,
        },
      ],
      providers: [
        {
          basePermissionToken: _VIEW_ORGANIZATION_USER_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_USER_PERMISSION_VIEW_GRANT,
          provider: VIEW_ORGANIZATION_USER_CANVIEW_PERMISSION,
        },
        {
          basePermissionToken: _VIEW_ORGANIZATION_USER_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_USER_PERMISSION_EDIT_GRANT,
          provider: VIEW_ORGANIZATION_USER_CANEDIT_PERMISSION,
        },
      ],
    };
  };
/**
 * @VIEW_ORGANIZATION_USER_TOKENS END
 * **/

/**
 * @VIEW_ORGANIZATION_STUDIES_TOKENS START
 * **/
export const VIEW_ORGANIZATION_STUDIES_CANVIEW_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_STUDIES_CANVIEW_PERMISSION',
  );
export const VIEW_ORGANIZATION_STUDIES_CANEDIT_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_STUDIES_CANEDIT_PERMISSION',
  );

export const VIEW_ORGANIZATION_STUDIES_PERMISSIONS =
  (): RolePermissionProvider => {
    const _VIEW_ORGANIZATION_STUDIES_BASE_PERMISSION =
      _generateInjectionToken<BasePermission>(
        'VIEW_ORGANIZATION_STUDIES_BASE_PERMISSION',
      );
    const _VIEW_ORGANIZATION_STUDIES_PERMISSION_VIEW_GRANT =
      _generateInjectionToken<PermissionGrants.CanView>(
        'VIEW_ORGANIZATION_STUDIES_PERMISSION_VIEW_GRANT',
      );
    const _VIEW_ORGANIZATION_STUDIES_PERMISSION_EDIT_GRANT =
      _generateInjectionToken<PermissionGrants.CanEdit>(
        'VIEW_ORGANIZATION_STUDIES_PERMISSION_EDIT_GRANT',
      );
    return {
      basePermissions: [
        {
          token: _VIEW_ORGANIZATION_STUDIES_BASE_PERMISSION,
          tokenValue: ViewOrganizationStudies,
        },
      ],
      grants: [
        {
          token: _VIEW_ORGANIZATION_STUDIES_PERMISSION_VIEW_GRANT,
          tokenValue: PermissionGrants.CanView,
        },
        {
          token: _VIEW_ORGANIZATION_STUDIES_PERMISSION_EDIT_GRANT,
          tokenValue: PermissionGrants.CanEdit,
        },
      ],
      providers: [
        {
          basePermissionToken: _VIEW_ORGANIZATION_STUDIES_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_STUDIES_PERMISSION_VIEW_GRANT,
          provider: VIEW_ORGANIZATION_STUDIES_CANVIEW_PERMISSION,
        },
        {
          basePermissionToken: _VIEW_ORGANIZATION_STUDIES_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_STUDIES_PERMISSION_EDIT_GRANT,
          provider: VIEW_ORGANIZATION_STUDIES_CANEDIT_PERMISSION,
        },
      ],
    };
  };
/**
 * @VIEW_ORGANIZATION_STUDIES_TOKENS END
 * **/

/**
 * @VIEW_ORGANIZATION_SITES_TOKENS START
 * **/
export const VIEW_ORGANIZATION_SITES_CANVIEW_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_SITES_CANVIEW_PERMISSION',
  );
export const VIEW_ORGANIZATION_SITES_CANEDIT_PERMISSION =
  _generateInjectionToken<Signal<boolean>>(
    'VIEW_ORGANIZATION_SITES_CANEDIT_PERMISSION',
  );

export const VIEW_ORGANIZATION_SITES_PERMISSIONS =
  (): RolePermissionProvider => {
    const _VIEW_ORGANIZATION_SITES_BASE_PERMISSION =
      _generateInjectionToken<BasePermission>(
        'VIEW_ORGANIZATION_SITES_BASE_PERMISSION',
      );
    const _VIEW_ORGANIZATION_SITES_PERMISSION_VIEW_GRANT =
      _generateInjectionToken<PermissionGrants.CanView>(
        'VIEW_ORGANIZATION_SITES_PERMISSION_VIEW_GRANT',
      );
    const _VIEW_ORGANIZATION_SITES_PERMISSION_EDIT_GRANT =
      _generateInjectionToken<PermissionGrants.CanEdit>(
        'VIEW_ORGANIZATION_SITES_PERMISSION_EDIT_GRANT',
      );
    return {
      basePermissions: [
        {
          token: _VIEW_ORGANIZATION_SITES_BASE_PERMISSION,
          tokenValue: ViewOrganizationSites,
        },
      ],
      grants: [
        {
          token: _VIEW_ORGANIZATION_SITES_PERMISSION_VIEW_GRANT,
          tokenValue: PermissionGrants.CanView,
        },
        {
          token: _VIEW_ORGANIZATION_SITES_PERMISSION_EDIT_GRANT,
          tokenValue: PermissionGrants.CanEdit,
        },
      ],
      providers: [
        {
          basePermissionToken: _VIEW_ORGANIZATION_SITES_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_SITES_PERMISSION_VIEW_GRANT,
          provider: VIEW_ORGANIZATION_SITES_CANVIEW_PERMISSION,
        },
        {
          basePermissionToken: _VIEW_ORGANIZATION_SITES_BASE_PERMISSION,
          grantsToken: _VIEW_ORGANIZATION_SITES_PERMISSION_EDIT_GRANT,
          provider: VIEW_ORGANIZATION_SITES_CANEDIT_PERMISSION,
        },
      ],
    };
  };
/**
 * @VIEW_ORGANIZATION_SITES_TOKENS END
 * **/
